import React, { useEffect, useRef, useState, useTransition } from "react";
import { ScrollRestoration } from "react-router-dom";
import App from "../App";
import { AliveScope } from "react-activation";
import utils from "../utils/utils";
import config from "../utils/config";
import locale from "../utils/locale";
import {
  getBrowserFingerPrintId,
  reportHomeAppActive,
  applyThirdPartyPwaSafeAreaFix,
} from "../utils/BrowserFingerprintId";
import { MusicContextProvider } from "../contexts/MusicContext";
import AppProvider from "../providers/AppProvider";
import { PageLoadingProvider } from "../contexts/PageLoadingContext";
import {
  loadConfig,
  useStartupService,
  useThemeService,
  useUserAuth,
} from "../modules";
import { ROUTES, useNavigation } from "../utils/navigation";
import { useStartupStore } from "../modules/startup/stores";
import { useWsInit } from "../hooks/useEvent";
import { ConfigProvider as AntdConfigProvider, theme as antdTheme } from "antd";
import { ConfigProvider as AntdMobileConfigProvider } from "antd-mobile";
import enUS from "antd-mobile/es/locales/en-US";
import ptBR from "antd-mobile/es/locales/pt-BR";
import esES from "antd-mobile/es/locales/es-ES";
import { setGlobalValue } from "../stores/GlobalState";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

const antdMobileLocaleMap: Record<string, typeof enUS> = {
  en: enUS,
  pt: ptBR,
  es: esES,
};

function AntdMobileLocaleProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const antdMobileLocale = antdMobileLocaleMap[i18n.language] || enUS;
  return (
    <AntdMobileConfigProvider locale={antdMobileLocale}>
      {children}
    </AntdMobileConfigProvider>
  );
}

config.init();
setGlobalValue("shownDialogIds", []);

function AppInitializer({ children }: { children: React.ReactNode }) {
  // setInitialized(true) 不能阻塞首页加载
  const [, startTransition] = useTransition();
  const [initialized, setInitialized] = useState(false);
  const [startupError, setStartupError] = useState<string | null>(null);
  const [shouldReplaceUrlToHome, setShouldReplaceUrlToHome] = useState(false);
  //翻译需要阻塞一下 否则页面会闪烁
  const [i18nReady, setI18nReady] = useState(
    i18n.isInitialized && i18n.hasResourceBundle(i18n.language, "translation")
  );

  const { loadStartupData } = useStartupService();
  const { loadTheme } = useThemeService();
  const { loadUserInfo, loadGuestInfo } = useUserAuth();
  const { navigate } = useNavigation();

  useWsInit();

  useEffect(() => {
    if (i18nReady) return;
    let cancelled = false;
    const markReady = () => {
      if (cancelled) return;
      if (
        i18n.isInitialized &&
        i18n.hasResourceBundle(i18n.language, "translation")
      ) {
        setI18nReady(true);
      }
    };
    i18n.on("initialized", markReady);
    i18n.on("loaded", markReady);
    i18n.on("languageChanged", markReady);
    markReady();
    // 兜底5秒超时 防止页面一直loading
    const fallbackTimer = setTimeout(() => {
      if (!cancelled) setI18nReady(true);
    }, 5000);
    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      i18n.off("initialized", markReady);
      i18n.off("loaded", markReady);
      i18n.off("languageChanged", markReady);
    };
  }, [i18nReady]);

  // 在 useEffect 中调用 loadConfig，避免在渲染期间更新状态
  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const startupResult = await loadStartupData(true);

        // 邀请码 / 红包码 / launchAgent 全部不阻塞首屏渲染
        const searchParams = new URLSearchParams(window.location.search);
        const pathMatch = window.location.pathname.match(/^\/c-(.+)$/);
        const inviteCode =
          searchParams.get("p") || (pathMatch ? pathMatch[1].trim() : null);
        const redpacketCode = searchParams.get("r");

        const chFromUrl = searchParams.get("ch") || utils.getQueryString("ch");
        const chFromCache = utils.getObject("ch") || window.__ch;
        const chToUse =
          (chFromUrl && chFromUrl.trim()) ||
          (chFromCache && String(chFromCache).trim()) ||
          "";
        if (chFromUrl && chFromUrl.trim()) {
          utils.setObject("ch", chFromUrl.trim());
          window.__ch = chFromUrl.trim();
        }
        // launchAgent 接口异步发起，**不入 parallelTasks**，不阻塞首屏渲染
        if (chToUse) {
          utils.fetchLaunchAgentAndCache(chToUse).catch(() => {});
        }

        const token = utils.getObject("token");
        const userInfo = utils.getUser();
        const isLoggedIn =
          (token && token !== "null" && token.length >= 10) ||
          (userInfo && typeof userInfo === "object" && userInfo !== null);

        if (redpacketCode) {
          sessionStorage.setItem("redpacketCode", redpacketCode);
        }

        if (!isLoggedIn && inviteCode) {
          utils.setObject("invitedCode", inviteCode);
          if (pathMatch) {
            setShouldReplaceUrlToHome(true);
          }
        }

        // FCP/LCP 优化:语言包加载改为 fire-and-forget。
        if (startupResult?.langItemsCacheKey) {
          utils
            .request("/static/lang/" + startupResult.langItemsCacheKey, null, {
              method: "GET",
            })
            .then(resp => {
              if (resp && resp.data && resp.data.model) {
                locale.register(resp.data.model, () => {});
              }
            })
            .catch(e => {
              console.warn("加载语言包失败:", e);
            });
        }

        // setInitialized 仅用于驱动 shouldReplaceUrlToHome 的 navigate gating，
        // 不再决定 children 是否渲染，所以包进 startTransition 让它走非紧急更新通道，
        // 不抢占首屏 paint。catch 路径同处理。
        startTransition(() => {
          setInitialized(true);
        });
      } catch (error) {
        console.error("应用初始化失败:", error);
        setStartupError(error instanceof Error ? error.message : "初始化失败");
        startTransition(() => {
          setInitialized(true);
        });
      }
    };

    initializeApp();
  }, [loadStartupData, loadTheme, loadUserInfo, loadGuestInfo]);

  useEffect(() => {
    if (initialized && shouldReplaceUrlToHome) {
      setShouldReplaceUrlToHome(false);
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [initialized, shouldReplaceUrlToHome, navigate]);

  if (startupError) {
    console.warn("启动过程中出现错误:", startupError);
  }
  // 不阻塞等 startup API 完成（perf 改造），但**等 i18n 就绪**才渲染子树，
  if (!i18nReady) {
    return null;
  }
  return <>{children}</>;
}

function Entry() {
  const [checked, setChecked] = useState(false);
  const appType = useStartupStore(s => s.startupData?.appType);
  // 防止 reportHomeAppActive 重复触发：appType 从 undefined → "h5" 变化时
  const reportedRef = useRef(false);
  useEffect(() => {
    setChecked(true);
    applyThirdPartyPwaSafeAreaFix();
  }, []);

  // 归因上报：必须等 appType 真实值到达才触发，否则 reportHomeAppActive 内部
  // `if (appType === "h5") trackLandingAttribution(...)` 会因 appType=undefined
  // 跳过，导致首页 h5 用户的 /api/tracking/landingads 广告归因上报丢失
  useEffect(() => {
    if (reportedRef.current) return;

    const runReport = async (typeForReport: string | undefined) => {
      if (reportedRef.current) return;
      reportedRef.current = true;
      try {
        // _cburl 同步读 window 全局，必须在 fingerprint 之前判断（保留原跳转逻辑）
        if (window._cburl) {
          window.location.href = window._cburl;
          return;
        }
        const fp =
          utils.getObject("BrowserFingerprintId") ||
          (await getBrowserFingerPrintId());
        await reportHomeAppActive(fp, typeForReport);
      } catch (err) {
        console.error("首页指纹初始化失败:", err);
      }
    };

    if (appType !== undefined) {
      // startupData 已到达（含 KeepAlive 二次激活复用 store 的场景），立即上报
      runReport(appType);
      return;
    }

    // 5s 兜底：避免 startupAPI 失败时永远不上报基础埋点
    const timer = setTimeout(() => runReport(undefined), 5000);
    return () => clearTimeout(timer);
  }, [appType]);

  if (checked) {
    const path = window.location.pathname;
    const willLandOnHome =
      path === ROUTES.HOME ||
      path === ROUTES.HOME_ALT ||
      /^\/c-(.+)$/.test(path);
    if (!willLandOnHome) {
      utils.hideInitialLoading();
    }
    return <App />;
  }
  return null;
}

export default function MainShell() {
  return (
    <AntdConfigProvider
      theme={{
        algorithm: antdTheme.defaultAlgorithm,
      }}
      prefixCls="antd"
    >
      <AntdMobileLocaleProvider>
        <PageLoadingProvider>
          <AppInitializer>
            <MusicContextProvider>
              <AppProvider>
                <AliveScope>
                  <Entry />
                </AliveScope>
              </AppProvider>
            </MusicContextProvider>
          </AppInitializer>
          <ScrollRestoration
            getKey={location => {
              if (
                location.pathname.includes(ROUTES.GAME_DETAIL) ||
                location.pathname === ROUTES.HOME ||
                location.pathname === ROUTES.HOME_ALT
              ) {
                return null;
              }
              return location.pathname;
            }}
          />
        </PageLoadingProvider>
      </AntdMobileLocaleProvider>
    </AntdConfigProvider>
  );
}
